import { on } from 'events';

import { cardEditContext } from '@context/card-edit-context';
import { cardModeContext } from '@context/card-mode-context';
import { getOrCreateContext } from '@context/singleton-manager';
import useNode from '@hooks/use-node';
import { Box } from '@ui-lib';
import {
  type DependencyList,
  type FormEvent,
  type KeyboardEvent,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

// These styles are shared between CardField and CardProperty. As such they have been moved to a shared file.
import styles from '../CardItem.module.scss';
import { DeleteDialog } from '../DeleteDialog';
import { EditDialog } from '../EditDialog';
import { EditIndicator } from '../EditIndicator';
import { LockIndicator } from '../LockIndicator';
import { ResponsiveDialog } from '../ResponsiveDialog';
import { UnlinkConfirmationDialog } from '../UnlinkConfirmationDialog';
import type { ValueProvider } from '.cache/__types__';

const outlineContext = getOrCreateContext('card-outline');

type DeletePropertyProps = {
  canDelete: boolean;
  onDelete: () => void;
};

type CardPropertyProps = {
  editUI: ReactElement;
  updateTempValueProviderForTour: (valueProvider: ValueProvider | null) => void;
  isValid?: boolean;
  attributeId: string;
  validator?: () => boolean;
  positionDeps?: DependencyList;
  /** Boolean for if the entire field is locked and therefore cannot be edited */
  isLocked?: boolean;
  isLockedTooltipMessage?: string;
  isFieldLinkedToProfile?: boolean;
  isOptional?: boolean;
  unlinkCardField?: (valueProvider?: ValueProvider) => void;
  onSave: () => void;
  onCancel: () => void;
  hasUnsavedChanges?: boolean;
  attributeDisplayName: string;
  attributeValue?: string;
  icon: ReactNode;
  trackingAttributeName: string;
} & DeletePropertyProps;

/**
 * A card Property is a single non-orderable, not repeatable attribute on a Card.
 * eg. a Department, Job Title, Name.
 */
export const CardProperty = ({
  icon,
  attributeDisplayName,
  attributeValue,
  editUI,
  updateTempValueProviderForTour,
  attributeId,
  validator,
  isFieldLinkedToProfile,
  isOptional,
  unlinkCardField,
  positionDeps = [],
  isLocked = false,
  isLockedTooltipMessage,
  canDelete,
  onDelete,
  onSave,
  hasUnsavedChanges,
  onCancel,
  children,
  trackingAttributeName,
}: PropsWithChildren<CardPropertyProps>) => {
  const [showDeletePrompt, setShowDeletePrompt] = useState<boolean>(false);
  const [showEditIcon, setShowEditIcon] = useState(false);
  const [showUnlinkWarningPrompt, setShowUnlinkWarningPrompt] = useState(false);
  const { activeId, requestToMakeActive, makeInactive, unlock: unlockOutline } = useContext(outlineContext);
  const {
    activeAttributeId,
    requestToMakeActive: requestToEdit,
    makeInactive: requestToHideEdit,
    showRequiredAttributesErrors,
    onEditEvent,
  } = useContext(cardEditContext);
  const { mode } = useContext(cardModeContext);

  const [fieldNode, fieldRef] = useNode<HTMLDivElement | HTMLButtonElement>();
  const [fieldBtnNode, fieldBtnRef] = useNode<HTMLDivElement | HTMLButtonElement>();

  const shouldMakeInactiveRef = useRef(false);

  const isActive = activeId == attributeId;
  const showDialog = activeAttributeId === attributeId;
  const showHoverStateOnActiveAttribute = activeAttributeId === attributeId;

  useEffect(() => {
    if (showDialog) {
      onEditEvent?.('open', attributeDisplayName);
    }
  }, [showDialog, onEditEvent, attributeDisplayName]);

  function handleMouseEnter() {
    if (mode !== 'edit') return;
    shouldMakeInactiveRef.current = false;
    requestToMakeActive(attributeId);
    setShowEditIcon(true);
  }

  function handleFocus() {
    if (mode !== 'edit') return;
    shouldMakeInactiveRef.current = false;
    requestToMakeActive(attributeId);
  }

  function handleBlur() {
    if (mode !== 'edit') return;
    makeInactive(attributeId);
  }

  function handleMouseLeave() {
    if (mode !== 'edit') return;
    shouldMakeInactiveRef.current = true;
    setShowEditIcon(false);
    setTimeout(() => {
      if (shouldMakeInactiveRef.current) makeInactive(attributeId);
    }, 300);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    // This must be keyDown otherwise submitting the form in the edit popover will reopen the edit popover
    if (event.key === 'Enter') {
      requestToEdit(attributeId, 'update');
    }
  }

  function handleFieldClick() {
    unlockOutline({ command: 'activate-last-request' });
    requestToEdit(attributeId, 'update');
  }

  const handleClose = useCallback(() => {
    onCancel();
    setShowDeletePrompt(false);
    requestToHideEdit(attributeId);
    fieldBtnNode?.focus();
  }, [onCancel, requestToHideEdit, attributeId, fieldBtnNode]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isFieldLinkedToProfile && hasUnsavedChanges) {
      // Show the unlink dialog if the fields are linked and changes have been made to the field
      return setShowUnlinkWarningPrompt(true);
    }

    onEditEvent?.('save', trackingAttributeName);
    onSave();
    handleClose();
  };

  const handleSubmitLinkedField = () => {
    unlinkCardField?.('member');
    handleClose();
    onEditEvent?.('save', trackingAttributeName);
    setShowUnlinkWarningPrompt(false);
  };

  const handleOnCancel = () => {
    onEditEvent?.('cancel', trackingAttributeName);
    handleClose();
  };

  const handleDelete = (event: FormEvent) => {
    event.preventDefault();
    onDelete();
    onEditEvent?.('delete', trackingAttributeName);
    handleClose();
  };

  const showDeleteDialog = () => {
    setShowDeletePrompt(true);
  };

  const hideDeleteDialog = () => {
    setShowDeletePrompt(false);
  };

  if (mode !== 'edit') {
    return <>{children}</>;
  }

  // The edit icon is outside the card-field outline, otherwise it gets distorted by the outline
  // as it morphs when changing which field is highlighted
  return (
    <>
      <Box
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={fieldRef}
        display="flex"
        justifyContent="spaceBetween"
        alignItems="center"
        className={styles['card-field']}
        data-required-error={showRequiredAttributesErrors && !attributeValue && !isOptional}
        data-active-attribute={showHoverStateOnActiveAttribute}
      >
        <button
          className={styles['card-field-btn']}
          data-id={attributeId}
          data-is-locked={isLocked}
          onBlur={handleBlur}
          onClick={handleFieldClick}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          ref={fieldBtnRef}
        >
          {children}
          {isLocked && isActive && <LockIndicator tooltipMessage={isLockedTooltipMessage} />}
        </button>

        {!isLocked && showEditIcon && <EditIndicator />}
      </Box>

      <ResponsiveDialog
        show={showDialog && !isLocked}
        updateTempValueProviderForTour={updateTempValueProviderForTour}
        handleClose={handleClose}
        targetNode={fieldNode}
        positionDeps={positionDeps}
      >
        {canDelete && showDeletePrompt ? (
          <DeleteDialog
            icon={icon}
            title={attributeDisplayName}
            onCancel={hideDeleteDialog}
            onClose={handleClose}
            onSubmit={handleDelete}
          />
        ) : showUnlinkWarningPrompt ? (
          <UnlinkConfirmationDialog
            title={attributeDisplayName}
            onCancel={() => setShowUnlinkWarningPrompt(false)}
            onSave={handleSubmitLinkedField}
          />
        ) : (
          <EditDialog
            icon={icon}
            title={attributeDisplayName}
            canDelete={canDelete}
            onCancel={handleOnCancel}
            onSubmit={handleSubmit}
            onDelete={showDeleteDialog}
            canSubmit={(validator?.() && !hasUnsavedChanges) ?? true}
          >
            {editUI}
          </EditDialog>
        )}
      </ResponsiveDialog>
    </>
  );
};
